import { FC, useEffect, useState } from 'react';
// import './styles.scss'
import styled from 'styled-components';
import { useWindowSize } from 'usehooks-ts';
import ContentGrid from 'components/ContentGrid';
import {
  ContentWidth,
  TextWidth,
  TextWidthExtended,
  TextWidthExtendedFullMobile,
  ArticleWidth
} from '@ta-interaktiv/react-disco-grid';

import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';

import { parseAnnotations, Annotation } from './annotationParser';
import AnnotationsOverlay from './AnnotationsOverlay';

/** allowing style attributes */
const sanitisationSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    '*': [
      ...(defaultSchema.attributes?.['*'] || []),
      'style',
    ],
  },
};

export const ImageSwitch: FC<{
  mobileImageUrl?: string;
  tabletImageUrl?: string;
  desktopImageUrl?: string;
  mobileImageUrlDark?: string;
  tabletImageUrlDark?: string;
  desktopImageUrlDark?: string;
  colorMode: 'dark' | 'light' | undefined;
  title?: string;
  lead?: string;
  width?: 'full' | 'content' | 'text' | 'textextended' | 'textextendedfullmobile' | 'article';
  caption?: string;
  source?: string;
  labelType?: 'top' | 'bottom';
  mobileAnnotations?: string;
  tabletAnnotations?: string;
  desktopAnnotations?: string;
}> = ({
  mobileImageUrl,
  tabletImageUrl,
  desktopImageUrl,
  mobileImageUrlDark,
  tabletImageUrlDark,
  desktopImageUrlDark,
  colorMode,
  title,
  lead,
  width,
  caption,
  source,
  labelType = 'top',
  mobileAnnotations,
  tabletAnnotations,
  desktopAnnotations,
}) => {
    const windowSize = useWindowSize();
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    useEffect(() => {
      setIsMobile(windowSize.width < 712);
      setIsTablet(windowSize.width >= 712 && windowSize.width < 1024);
    }, [windowSize.width]);

    const imageurlmob =
      colorMode === 'dark' && mobileImageUrlDark
        ? mobileImageUrlDark
        : mobileImageUrl;

    const imageurltab =
      colorMode === 'dark' && tabletImageUrlDark
        ? tabletImageUrlDark
        : tabletImageUrl;

    const imageurldesk =
      colorMode === 'dark' && desktopImageUrlDark
        ? desktopImageUrlDark
        : desktopImageUrl;

    let annotationString = "";

    if (isMobile) {
      annotationString = mobileAnnotations || "";
    } else if (isTablet) {
      annotationString = tabletAnnotations || "";
    } else {
      annotationString = desktopAnnotations || "";
    }


    const annotations = parseAnnotations(annotationString);

    return (
      <>
        {(title || caption) && labelType === 'top' && (
          <TextWidth>
            <>
              {title && <Title><ReactMarkdown rehypePlugins={[[rehypeRaw], [rehypeSanitize, sanitisationSchema]]}>{title}</ReactMarkdown></Title>}
              {lead && <Lead><ReactMarkdown rehypePlugins={[[rehypeRaw], [rehypeSanitize, sanitisationSchema]]}>{lead}</ReactMarkdown></Lead>}
            </>
          </TextWidth>
        )}

        {width === 'full' && (
          <Image
            isMobile={isMobile}
            isTablet={isTablet}
            mobile={imageurlmob}
            tablet={imageurltab}
            desktop={imageurldesk}
            mobileAnnotations={mobileAnnotations}
            tabletAnnotations={tabletAnnotations}
            desktopAnnotations={desktopAnnotations}
          />
        )}
        {width === 'content' && (
          <ContentWidth>
            <Image
              isMobile={isMobile}
              isTablet={isTablet}
              mobile={imageurlmob}
              tablet={imageurltab}
              desktop={imageurldesk}
              mobileAnnotations={mobileAnnotations}
              tabletAnnotations={tabletAnnotations}
              desktopAnnotations={desktopAnnotations}
            />
          </ContentWidth>
        )}
        {width === 'text' && (
          <TextWidth>
            <Image
              isMobile={isMobile}
              isTablet={isTablet}
              mobile={imageurlmob}
              tablet={imageurltab}
              desktop={imageurldesk}
              mobileAnnotations={mobileAnnotations}
              tabletAnnotations={tabletAnnotations}
              desktopAnnotations={desktopAnnotations}
            />
          </TextWidth>
        )}
        {width === 'textextended' && (
          <TextWidthExtended>
            <Image
              isMobile={isMobile}
              isTablet={isTablet}
              mobile={imageurlmob}
              tablet={imageurltab}
              desktop={imageurldesk}
              mobileAnnotations={mobileAnnotations}
              tabletAnnotations={tabletAnnotations}
              desktopAnnotations={desktopAnnotations}
            />
          </TextWidthExtended>
        )}
        {width === 'textextendedfullmobile' && (
          <TextWidthExtendedFullMobile>
            <Image
              isMobile={isMobile}
              isTablet={isTablet}
              mobile={imageurlmob}
              tablet={imageurltab}
              desktop={imageurldesk}
              mobileAnnotations={mobileAnnotations}
              tabletAnnotations={tabletAnnotations}
              desktopAnnotations={desktopAnnotations}
            />
          </TextWidthExtendedFullMobile>
        )}
        {width === 'article' && (
          <ArticleWidth>
            <Image
              isMobile={isMobile}
              isTablet={isTablet}
              mobile={imageurlmob}
              tablet={imageurltab}
              desktop={imageurldesk}
              mobileAnnotations={mobileAnnotations}
              tabletAnnotations={tabletAnnotations}
              desktopAnnotations={desktopAnnotations}
            />
          </ArticleWidth>
        )}

        {(caption || source) && (
          <TextWidth>
            <>ˆ
              {labelType === 'bottom' && (
                <>
                  {title && <Title className='bottom'><ReactMarkdown rehypePlugins={[[rehypeRaw], [rehypeSanitize, sanitisationSchema]]}>{title}</ReactMarkdown></Title>}
                </>
              )}
            </>
            <Caption className={labelType === 'top' ? 'top' : ''}>
              {(labelType === 'bottom' || labelType === 'top') && caption && <ReactMarkdown rehypePlugins={[[rehypeRaw], [rehypeSanitize, sanitisationSchema]]}>{caption}</ReactMarkdown>}
              {source && <Source><ReactMarkdown>{source}</ReactMarkdown></Source>}
            </Caption>
          </TextWidth>
        )}
      </>
    );
  };

const Image = ({
  mobile,
  tablet,
  desktop,
  isMobile,
  isTablet,
  mobileAnnotations,
  tabletAnnotations,
  desktopAnnotations,
}: {
  mobile?: string;
  tablet?: string;
  desktop?: string;
  isMobile: boolean;
  isTablet: boolean;
  mobileAnnotations?: string;
  tabletAnnotations?: string;
  desktopAnnotations?: string;
}) => {
  let src = desktop;
  let annotationString = desktopAnnotations || "";

  if (isMobile && mobile) {
    src = mobile;
    annotationString = mobileAnnotations || desktopAnnotations || "";
  } else if (isTablet && tablet) {
    src = tablet;
    annotationString = tabletAnnotations || desktopAnnotations || "";
  }

  const annotations = parseAnnotations(annotationString);

  return (
    <ImageContainer className='ImageContainer' style={{ position: "relative" }}>
      <img src={src} />
      <AnnotationsOverlay annotations={annotations} />
    </ImageContainer>
  );
};
{
  /* <video
          ref={vidRef}
          controls
          // autoPlay
          muted
          playsInline
          loop
          poster={isMobile ? mobilePoster : desktopPoster}
        >
          {isMobile && <source src={`${mobileVideo}`} type='video/mp4' />}
          <source src={`${desktopVideo}`} type='video/mp4' />
        </video> */
}
const Title = styled.h3`
  color: var(--text-color);
  margin: 0;
  font-size: 18px !important;
  line-height: 2.4rem;
  margin-bottom: 0.5rem;

  &.bottom {
    margin-top: 1.5rem;
    font-size: 1.5rem !important;
    line-height: 2.1rem;
    margin-bottom: 0;
  }
`;
const Lead = styled.div`
  color: var(--text-color);
  font-size: 15px;
  width: 100%;
  font-style: normal;
  font-weight: 400;
  line-height: 2.3rem;
  margin: 0;
  margin-bottom: 1rem;
  &.bottom {
    line-height: 2.1rem;
    margin-bottom: 0;
  }
`;

const Caption = styled.figcaption`
  color: var(--text-color);
  font-family: var(--font-plex);
  font-size: 1.5rem;
  line-height: 2.1rem;

  &.top {
    margin-top: 1.5rem;
  }

  .source {
    margin-top: 0.5em;
    font-weight: 400;
    color: var(--gray-3);
    font-size: 1.2rem;
    line-height: 1.8rem;
  }
`;
const Source = styled.div`
  margin-top: 0.5em;
  font-weight: 400;
  color: var(--gray-3);
  font-size: 1.2rem;
  line-height: 1.8rem;
  &.bottom {
    margin-top: 0.3rem;
  }
`;
const ImageContainer = styled.div<{ tenant?: string; }>`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-column: 1/-1;
  width: 100%;
  container-type: inline-size;
  img {
    width: 100%;
    height: auto;
  }
`;
